import { useState, type ComponentProps, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { LinkCashEventDialog } from '../dialogs/link-cash-event-dialog';
import { RecordPaymentDialog } from '../dialogs/record-payment-dialog';
import { invoiceActionAvailable } from '../lib/invoice-actions';

import type { InvoiceRow } from '../types';

// The Settlement group's single verb: Add payment covers manual entry and
// matching a cash-ledger event. While a cash event is linkable the button
// offers the source choice; once the invoice is fully Paid only manual entry
// remains and the button opens it directly.
export function AddPaymentControl({
  invoice,
  onChanged,
}: Readonly<{ invoice: InvoiceRow; onChanged: () => void }>) {
  const [recordOpen, setRecordOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const linkable = invoiceActionAvailable(invoice, 'link_cash');
  const trigger = (
    <AddPaymentTrigger
      invoiceId={invoice.id}
      onClick={
        linkable
          ? undefined
          : () => {
              setRecordOpen(true);
            }
      }
    />
  );
  return (
    <>
      {linkable ? (
        <SourceMenu
          invoiceId={invoice.id}
          trigger={trigger}
          onManual={() => {
            setRecordOpen(true);
          }}
          onCashEvent={() => {
            setLinkOpen(true);
          }}
        />
      ) : (
        trigger
      )}
      <PaymentDialogs
        invoice={invoice}
        onChanged={onChanged}
        recordOpen={recordOpen}
        setRecordOpen={setRecordOpen}
        linkOpen={linkOpen}
        setLinkOpen={setLinkOpen}
      />
    </>
  );
}

function PaymentDialogs({
  invoice,
  onChanged,
  recordOpen,
  setRecordOpen,
  linkOpen,
  setLinkOpen,
}: Readonly<{
  invoice: InvoiceRow;
  onChanged: () => void;
  recordOpen: boolean;
  setRecordOpen: (open: boolean) => void;
  linkOpen: boolean;
  setLinkOpen: (open: boolean) => void;
}>) {
  return (
    <>
      <RecordPaymentDialog
        open={recordOpen}
        onOpenChange={setRecordOpen}
        invoice={invoice}
        onRecorded={onChanged}
      />
      {linkOpen && (
        <LinkCashEventDialog
          open
          onOpenChange={setLinkOpen}
          invoice={invoice}
          onLinked={onChanged}
        />
      )}
    </>
  );
}

// Forwards the rest props so the dropdown trigger's `asChild` handlers (open
// on pointer/keyboard) reach the underlying button when a source menu wraps
// this trigger.
function AddPaymentTrigger({
  invoiceId,
  ...rest
}: { readonly invoiceId: string } & ComponentProps<typeof Button>) {
  return (
    <Button
      {...rest}
      className="w-full sm:w-auto"
      data-testid={`invoice-detail-${invoiceId}-add-payment`}
    >
      Add payment
    </Button>
  );
}

function SourceMenu({
  invoiceId,
  trigger,
  onManual,
  onCashEvent,
}: Readonly<{
  invoiceId: string;
  trigger: ReactNode;
  onManual: () => void;
  onCashEvent: () => void;
}>) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem
          data-testid={`invoice-detail-${invoiceId}-add-payment-manual`}
          onSelect={onManual}
        >
          Enter manually
        </DropdownMenuItem>
        <DropdownMenuItem
          data-testid={`invoice-detail-${invoiceId}-add-payment-cash-event`}
          onSelect={onCashEvent}
        >
          Match a cash-ledger event
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
